import { useEffect, useRef, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { CaretDown, CheckCircle, WarningCircle } from '@phosphor-icons/react';
import { site } from '../../content/site';
import { Button } from '../ui/Button';
import { SolarDisc } from '../principles/SolarDisc';

type Status = 'idle' | 'sending' | 'success' | 'error';
type Checked = 'name' | 'email' | 'message';
type Errors = Partial<Record<Checked, string>>;

const FORM = site.contact.form;
const CHECKED: Checked[] = ['name', 'email', 'message'];
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function validateField(name: Checked, raw: string): string | undefined {
	const value = raw.trim();
	if (name === 'email') return EMAIL_PATTERN.test(value) ? undefined : FORM.errors.email;
	return value ? undefined : FORM.errors[name];
}

function validate(data: FormData): Errors {
	const errors: Errors = {};
	CHECKED.forEach((name) => {
		const error = validateField(name, String(data.get(name) ?? ''));
		if (error) errors[name] = error;
	});
	return errors;
}

/** No-JS return path: Formspree redirects to `_next` (/?success=true#contact). */
function initialStatus(): Status {
	return new URLSearchParams(window.location.search).get('success') === 'true' ? 'success' : 'idle';
}

function describedBy(...ids: Array<string | false | undefined>): string | undefined {
	const list = ids.filter(Boolean).join(' ');
	return list || undefined;
}

// Dusk fields: rgba(247,247,240,0.06) fill, Mineral ink, 1px Mineral/15 border, Solar focus.
const LABEL = 'block text-[15px] font-medium leading-none text-mineral';
const CONTROL =
	'block w-full rounded-field border bg-mineral/[0.06] px-4 text-base text-mineral caret-solar transition-colors duration-300 ease-expo hover:border-mineral/30 focus:border-solar/70 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solar [&:-webkit-autofill]:shadow-[inset_0_0_0_100px_#26322B] [&:-webkit-autofill]:[-webkit-text-fill-color:#F7F7F0]';
const BORDER_OK = 'border-mineral/15';
const BORDER_ERROR = 'border-solar/80';
const NOTE = 'mt-2 text-sm leading-snug text-dusk-muted';

function FieldError({ id, children }: { id: string; children?: ReactNode }) {
	if (!children) return null;
	return (
		<p id={id} className="mt-2 flex items-start gap-1.5 text-sm leading-snug text-solar">
			<WarningCircle aria-hidden size={16} weight="bold" className="mt-px shrink-0" />
			<span>{children}</span>
		</p>
	);
}

/**
 * Contact form, Formspree integration kept as is (same action, method and field names).
 * Progressive enhancement: the form posts natively without the handler; with it, the
 * submit is validated inline and sent with fetch. States: idle, sending, success, error.
 */
export function ContactForm() {
	const [status, setStatus] = useState<Status>(initialStatus);
	const [errors, setErrors] = useState<Errors>({});
	const attempted = useRef(false);
	const sentFromHere = useRef(false);
	const mounted = useRef(true);
	const successRef = useRef<HTMLDivElement>(null);
	const { labels } = FORM;

	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
		};
	}, []);

	// Move focus to the confirmation after a submit (not on the ?success=true return).
	useEffect(() => {
		if (status === 'success' && sentFromHere.current) successRef.current?.focus();
	}, [status]);

	const onChange = (event: FormEvent<HTMLFormElement>) => {
		const target = event.target as HTMLInputElement;
		const name = target.name as Checked;
		if (!attempted.current || !CHECKED.includes(name)) return;
		setErrors((prev) => ({ ...prev, [name]: validateField(name, target.value) }));
	};

	const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (status === 'sending') return;
		const form = event.currentTarget;
		const data = new FormData(form);
		const nextErrors = validate(data);
		attempted.current = true;
		setErrors(nextErrors);

		const firstInvalid = CHECKED.find((name) => nextErrors[name]);
		if (firstInvalid) {
			(form.elements.namedItem(firstInvalid) as HTMLElement | null)?.focus();
			return;
		}

		setStatus('sending');
		try {
			const response = await fetch(FORM.action, {
				method: 'POST',
				body: data,
				headers: { Accept: 'application/json' },
			});
			if (!mounted.current) return;
			sentFromHere.current = response.ok;
			setStatus(response.ok ? 'success' : 'error');
		} catch {
			if (mounted.current) setStatus('error');
		}
	};

	// Success is announced by moving focus to the confirmation, so it is not repeated here.
	const live = status === 'sending' ? FORM.sending : status === 'error' ? FORM.failure : '';
	const sending = status === 'sending';

	return (
		<div
			data-contact-panel
			className="relative isolate overflow-hidden rounded-card border border-mineral/10 bg-mineral/[0.04] p-6 backdrop-blur-xl [color-scheme:dark] sm:p-8 xl:p-10"
		>
			<p role="status" aria-live="polite" className="sr-only">
				{live}
			</p>

			{status === 'success' ? (
				<div
					ref={successRef}
					tabIndex={-1}
					className="flex min-h-[26rem] flex-col justify-between gap-16 pb-16 outline-none sm:pb-0"
				>
					{/* A sun rising from the panel's bottom edge. */}
					<SolarDisc tone="dusk" className="-bottom-[9rem] -right-12 -z-10 w-[18rem] sm:-right-8" />
					<CheckCircle aria-hidden size={44} weight="light" className="text-solar" />
					<p className="max-w-[18ch] font-display text-[clamp(1.75rem,2.6vw,2.5rem)] font-medium leading-[1.08] tracking-[-0.02em] text-mineral">
						{FORM.success}
					</p>
				</div>
			) : (
				<form
					action={FORM.action}
					method="POST"
					noValidate
					onSubmit={onSubmit}
					onChange={onChange}
					aria-busy={sending}
					className="grid gap-x-4 gap-y-7 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2"
				>
					<input type="hidden" name="_subject" value={FORM.emailSubject} />
					<input type="hidden" name="_next" value={FORM.next} />
					{/* Honeypot: invisible to people and assistive tech, filled only by bots. */}
					<input
						type="text"
						name="_gotcha"
						tabIndex={-1}
						autoComplete="off"
						aria-hidden="true"
						className="absolute -left-[10000px] top-auto h-px w-px overflow-hidden opacity-0"
					/>

					<div>
						<label htmlFor="contact-name" className={LABEL}>
							{labels.name}
						</label>
						<input
							id="contact-name"
							name="name"
							type="text"
							required
							autoComplete="name"
							aria-invalid={errors.name ? true : undefined}
							aria-describedby={describedBy(errors.name && 'contact-name-error')}
							className={`mt-3 h-[3.25rem] ${CONTROL} ${errors.name ? BORDER_ERROR : BORDER_OK}`}
						/>
						<FieldError id="contact-name-error">{errors.name}</FieldError>
					</div>

					<div>
						<label htmlFor="contact-email" className={LABEL}>
							{labels.email}
						</label>
						<input
							id="contact-email"
							name="email"
							type="email"
							inputMode="email"
							required
							autoComplete="email"
							spellCheck={false}
							aria-invalid={errors.email ? true : undefined}
							aria-describedby={describedBy(errors.email && 'contact-email-error')}
							className={`mt-3 h-[3.25rem] ${CONTROL} ${errors.email ? BORDER_ERROR : BORDER_OK}`}
						/>
						<FieldError id="contact-email-error">{errors.email}</FieldError>
					</div>

					<div>
						<div className="flex items-baseline justify-between gap-3">
							<label htmlFor="contact-phone" className={LABEL}>
								{labels.phone}
							</label>
							<span id="contact-phone-hint" className="text-[13px] leading-none text-dusk-muted">
								{labels.phoneHint}
							</span>
						</div>
						<input
							id="contact-phone"
							name="phone"
							type="tel"
							inputMode="tel"
							autoComplete="tel"
							aria-describedby="contact-phone-hint"
							className={`mt-3 h-[3.25rem] ${CONTROL} ${BORDER_OK}`}
						/>
					</div>

					<div>
						<label htmlFor="contact-subject" className={LABEL}>
							{labels.subject}
						</label>
						<div className="relative mt-3">
							<select
								id="contact-subject"
								name="subject"
								defaultValue={FORM.subjectOptions[0]}
								className={`h-[3.25rem] cursor-pointer appearance-none pr-12 ${CONTROL} ${BORDER_OK}`}
							>
								{FORM.subjectOptions.map((option) => (
									<option key={option} value={option} className="bg-forest-night text-mineral">
										{option}
									</option>
								))}
							</select>
							<CaretDown
								aria-hidden
								size={18}
								weight="bold"
								className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-dusk-muted"
							/>
						</div>
					</div>

					<div className="col-span-full">
						<label htmlFor="contact-message" className={LABEL}>
							{labels.message}
						</label>
						<textarea
							id="contact-message"
							name="message"
							required
							rows={5}
							aria-invalid={errors.message ? true : undefined}
							aria-describedby={describedBy('contact-message-helper', errors.message && 'contact-message-error')}
							className={`mt-3 min-h-[9.5rem] resize-y py-3.5 leading-relaxed ${CONTROL} ${
								errors.message ? BORDER_ERROR : BORDER_OK
							}`}
						/>
						<p id="contact-message-helper" className={NOTE}>
							{FORM.helper.message}
						</p>
						<FieldError id="contact-message-error">{errors.message}</FieldError>
					</div>

					<div className="col-span-full grid gap-5 pt-1">
						{status === 'error' && (
							<p className="flex items-start gap-3 rounded-field border border-solar/30 bg-solar/[0.06] px-4 py-3.5 text-[15px] leading-snug text-mineral">
								<WarningCircle aria-hidden size={20} weight="bold" className="mt-px shrink-0 text-solar" />
								<span>{FORM.failure}</span>
							</p>
						)}
						<div>
							{/* Not disabled while sending (a disabled button drops keyboard focus); the submit
							    handler ignores repeat submits instead. */}
							<Button
								type="submit"
								variant="dusk"
								size="lg"
								arrow={sending ? undefined : 'go'}
								className={`w-full sm:w-auto ${sending ? 'cursor-progress opacity-80' : ''}`}
							>
								{sending ? FORM.sending : FORM.submit}
							</Button>
						</div>
					</div>
				</form>
			)}
		</div>
	);
}
